// src/models/caseStudiesData.js
// MODEL LAYER — Case studies data following the existing portfolio structure

import telzenCoreJourney from '../assets/case-studies/telzen-core-journey.png';
import telzenPackageDiscovery from '../assets/case-studies/telzen-package-discovery.png';
import telzenCheckoutFlow from '../assets/case-studies/telzen-checkout-flow.png';
import telzenDeviceCompatibility from '../assets/case-studies/telzen-device-compatibility.png';
import telzenMyEsim from '../assets/case-studies/telzen-my-esim.jpg';
import telzenPhysicalSim from '../assets/case-studies/telzen-physical-sim.jpg';
import telzenMarketingAnalytics from '../assets/case-studies/telzen-marketing-analytics.png';

import wisperbotArchitecture from '../assets/case-studies/wisperbot-architecture.png';
import wisperbotCustomerSdkChat from '../assets/case-studies/wisperbot-customer-sdk-chat.png';
import wisperbotAgentPostScheduling from '../assets/case-studies/wisperbot-agent-post-scheduling.png';
import wisperbotAgentSettings from '../assets/case-studies/wisperbot-agent-settings.png';

export const caseStudies = [
  {
    id: 1,
    slug: 'telzen',
    title: 'Telzen - eSIM & Physical SIM Management Platform',
    category: 'Mobile Application',
    date: 'Oct 2026',
    image: telzenPackageDiscovery,
    description:
      'A comprehensive case study on building Telzen, a Flutter application unifying real-time eSIM purchases, physical SIM delivery, multi-currency localized checkout, and device hardware compatibility.',
    tags: ['flutter', 'esim', 'payment', 'architecture', 'getx'],
    developer: 'Ismail Jabiulla',
    role: 'Software Engineer',
    platforms: 'Android & iOS',
    tech: [
      'Flutter',
      'Dart',
      'GetX',
      'Clean Architecture',
      'EPS (bKash & Nagad)',
      'Stripe',
      'SurjoPay',
      'PayPal',
      'Platform Channels',
      'Firebase Analytics',
      'Meta App Events',
    ],
    links: {
      appstore: 'https://tinyurl.com/bdsrazpn',
      playstore: 'https://tinyurl.com/79vr996b',
      github: null,
      pubdev: null,
    },
    sections: [
      {
        title: 'Project Overview',
        paragraphs: [
          'Telzen is a Flutter application that brings mobile data purchasing and SIM management into one experience. Customers can explore destination packages, complete checkout, access eSIM installation options, and manage purchased data through dedicated eSIM and physical SIM sections.',
          'The project’s central engineering challenge is continuity: the package selected during browsing must remain consistent through pricing, payment, installation or delivery, and subsequent top-ups. Telzen addresses this through shared purchase components and separate rules for each SIM type.',
          'Purchasing a data package is only one step in getting connected. Customers also need to understand coverage and validity, choose the correct SIM, complete setup, and recognise which actions remain available after a package expires or an order changes status. Physical SIM purchases introduce delivery and return states alongside these requirements.',
        ],
        image: telzenCoreJourney,
        imageCaption: 'The core journey: Discover → Purchase → Set up → Manage',
        techFoundation:
          'Flutter and Dart provide the application layer. GetX controllers manage screen state and dependencies; feature modules organise presentation, use cases, repositories, and API services. Platform channels connect the eSIM flow to native setup entry points.',
      },
      {
        title: 'From Package to Purchase',
        paragraphs: [
          'Destination packages lead into an order summary that brings coverage, validity, pricing, coupons, gifting, and payment selection together.',
          'Customers compare data allowances, validity periods, and displayed prices for the selected destination. The order summary exposes coverage and validity alongside coupon, gift, and payment controls.',
        ],
        screens: [
          {
            image: telzenPackageDiscovery,
            caption: '01 Package discovery: Customers compare data allowances, validity periods, and displayed prices.',
          },
          {
            image: telzenCheckoutFlow,
            caption: '02 Checkout: Order summary exposes coverage and validity alongside coupon, gift, and payment controls.',
          },
        ],
        note: 'Package prices appear in USD, while checkout is shown in BDT, illustrating the two supported currency presentations.',
      },
      {
        title: 'Checkout and Payment Experience',
        paragraphs: [
          'Customers search by destination and explore country or regional packages. Package details show coverage, data allowance, type, and validity. New physical SIM orders require a delivery address where applicable, while refill requests retain the selected SIM as their target.',
          'Telzen supports USD and BDT preferences. It restores a saved currency or chooses an initial value using country information. Checkout payment options depend on the customer’s country and SIM type; changing the payment option refreshes the quote and rechecks coupon eligibility.',
        ],
        table: {
          headers: ['Customer Context', 'Checkout Options'],
          rows: [
            ['Bangladesh', 'Pay in BDT through EPS (bKash, Nagad, cards), or Pay International through Stripe in USD.'],
            ['Other countries', 'Stripe, with PayPal additionally available for eSIM purchases.'],
          ],
        },
        challenges: [
          {
            title: 'Quotes and coupon handling',
            text: 'The order summary uses a server-provided quote for the package amount, discount, and payable total, with a SIM price for new physical SIM purchases. Payment initiation uses the accepted quote identifier. A fresh quote is requested before payment; a changed total or currency requires another review and checkout tap. Coupons are available in both SIM purchase flows. An already-used coupon is removed and the quote is retried once, while unrelated quote errors retain their own failure path.',
          },
          {
            title: 'eSIM gift purchases',
            text: 'Eligible eSIM purchases offer a gift option with a recipient email address. Entering that address changes the order to a gift and refreshes its quote. The request includes the recipient email and excludes an existing eSIM target, keeping gifting separate from a top-up. Removing the gift clears the recipient and recalculates the purchase.',
          },
        ],
      },
      {
        title: 'Installation and SIM Management',
        paragraphs: [
          'The installation confirmation sheet explains destination activation, roaming, and the single-installation constraint. Supported devices offer direct setup after instruction acknowledgement, alongside QR access. Unsupported devices receive a support message and QR access.',
          'Android and iOS use platform-specific setup entry points; provisioning depends on the device and operating system.',
          'SIM lists expose balances, package details, and refill actions. Status rules move inactive entries lower in the list, hide unavailable actions, and disable returned or cancelled physical SIM cards.',
        ],
        screens: [
          {
            image: telzenDeviceCompatibility,
            caption: '03 Device compatibility: The app displays eSIM support, model, manufacturer, platform, and OS.',
          },
          {
            image: telzenMyEsim,
            caption: '04 My eSIM: Purchased eSIM with allowance, expiry, installation action, and roaming guidance.',
          },
          {
            image: telzenPhysicalSim,
            caption: '05 Physical SIM: Data packs and refill options connecting an existing SIM to ongoing management.',
          },
        ],
      },
      {
        title: 'Key Engineering Decisions',
        paragraphs: [
          'Feature boundaries follow product responsibilities: The project groups code around authentication, home discovery, profile, and SIM features. Screens and GetX view models coordinate presentation, use cases express operations, and repositories connect those operations to data services.',
          'Keep concurrent catalog requests independent: Country and region controllers store results and loading state by SIM type. An eSIM request and a physical SIM request can therefore complete in either order without one result replacing the other type’s catalog.',
          'Separate fetching from empty content: My eSIM and My SIM use skeleton lists while initial data is pending. The first view communicates that content is loading before presenting an empty result.',
          'Synchronise changes across related screens: Changing the selected eSIM updates local selection state, refreshes the home balance, and refreshes related SIM data, while deduplicating in-flight requests.',
          'Refund eligibility follows purchase order: Physical SIM refund selection uses individual orders. Non-refunded top-ups block the base package, non-EPS orders require backend refundable flag, and EPS orders direct customers to support.',
        ],
      },
      {
        title: 'Marketing and Analytics Integration',
        paragraphs: [
          'Deep links and referral attribution: Recognised package links open country or regional catalogs with the relevant SIM type. Affiliate links record hits, save referral codes, and include them at signup to associate registration with the affiliate.',
          'Meta App Events & Firebase: Integration covers app activation, registration, content viewing, checkout initiation, and purchases. Purchase events include amount, currency, order identifier, payment method, and gift status.',
        ],
        image: telzenMarketingAnalytics,
        imageCaption: 'Navigation and measurement paths across acquisition, discovery, and conversion',
      },
      {
        title: 'Validation & Engineering Lessons',
        paragraphs: [
          'A focused test run verified all 20 tests across six test suites covering checkout quotes, catalog isolation, SIM list ordering, delivery status mapping, and refund requests.',
          'Engineering lesson: A SIM application needs more than successful purchase screens. Reliability also depends on what happens when requests finish out of order, a price changes, a device cannot install an eSIM, or a purchased item becomes inactive. Telzen’s implementation shows how those conditions can be handled within the same customer journey.',
        ],
        table: {
          headers: ['Test Area', 'Behaviour Covered'],
          rows: [
            ['Checkout quotes', 'New-purchase and top-up request fields, coupon recovery, and reconfirmation when payable total changes.'],
            ['Catalog isolation', 'Separate country and region results when eSIM and physical SIM requests finish in different orders.'],
            ['SIM list ordering', 'Status-based placement and preserved selection for both SIM types, plus date ordering for eSIMs.'],
            ['Delivery status', 'Mapping delivery and return states to physical SIM labels, including missing-status handling.'],
            ['Refund requests', 'Submitting the selected order identifier and preserving unsuccessful or rejected response states.'],
          ],
        },
      },
    ],
  },

  {
    id: 2,
    slug: 'wisperbot',
    title: 'WisperBot - Flutter Support SDK & Mobile Workspace',
    category: 'Flutter SDK & App',
    date: 'Oct 2026',
    image: wisperbotCustomerSdkChat,
    description:
      'An engineering case study on designing and publishing the WisperBot open source Flutter SDK (pub.dev) and shipping the agent mobile workspace on Android and iOS.',
    tags: ['flutter', 'sdk', 'chat', 'socket', 'open-source'],
    developer: 'Ismail Jabiulla',
    role: 'Flutter SDK & Mobile Application Developer',
    platforms: 'pub.dev, Android & iOS',
    tech: [
      'Flutter',
      'Dart',
      'pub.dev Publishing',
      'WebSockets',
      'Socket.io',
      'REST API',
      'Push Notifications',
      'Clean Architecture',
      'Offline Persistence',
    ],
    links: {
      pubdev: 'https://pub.dev/packages/wisperbot_chat',
      github: null,
      playstore: 'https://play.google.com',
      appstore: 'https://apps.apple.com',
    },
    sections: [
      {
        title: 'Project Overview & Ownership',
        paragraphs: [
          'WisperBot connects the customer chat experience with the mobile workspace agents use to manage support. I developed and released the Flutter SDK and agent application, delivering software for both developers integrating chat and teams responding to customers.',
          'My contribution covered Flutter SDK development, the agent mobile application, integration work, testing, and publication. I published the open source SDK on pub.dev (wisperbot_chat) and released the mobile app on Google Play and the Apple App Store.',
        ],
        table: {
          headers: ['Project Detail', 'Value'],
          rows: [
            ['Technology', 'Flutter and Dart'],
            ['Products', 'Customer chat SDK and agent mobile application'],
            ['Platforms', 'pub.dev, Android and iOS'],
            ['Lead Developer', 'Ismail Jabiulla'],
          ],
        },
      },
      {
        title: 'The Problem and Product Goals',
        paragraphs: [
          'Customer support involves two connected experiences. Customers need a convenient way to ask for help inside the application they already use. Agents need enough context and control to respond from a phone. Developers also need an integration that can be understood and maintained.',
          'The SDK side: The SDK packages a customer chat interface for use inside a Flutter application. As shown in the Telzen integration, it provides branded support messaging, suggested questions, timestamps, delivery indicators, resolution activity, and media entry points.',
          'The agent side: The WisperBot mobile application presents a conversation inbox alongside email, comments, live visitors, and post scheduling. Notifications and workspace controls help agents find relevant work, while statuses and statistics give additional context.',
        ],
        image: wisperbotArchitecture,
        imageCaption: 'Figure 1: Conceptual relationship between customer integration, platform services, and agent application',
      },
      {
        title: 'Designing the Flutter SDK',
        paragraphs: [
          'An SDK has two audiences: the developer integrating it and the person using its interface. Its design needs to make setup clear while giving customers a familiar support experience inside the host application.',
          'Suggested questions provide clear starting points. Timestamps and delivery indicators make conversation state visible. The attachment picker groups document, camera, gallery, and audio options, while the support header gives the conversation a recognizable identity.',
          'The SDK operates inside another application and must fit its navigation and lifecycle without memory leaks or gesture conflicts, handling automatic reconnection and stream disposal cleanly.',
        ],
        screens: [
          {
            image: wisperbotCustomerSdkChat,
            caption: 'Figure 2: Customer support interface embedded in Telzen using the WisperBot SDK',
          },
        ],
      },
      {
        title: 'Engineering the Agent Mobile Application',
        paragraphs: [
          'The agent application is the operational side of WisperBot, engineered for Android and iOS to bring conversation management and supporting workflows into a mobile workspace. Its engineering scope extends beyond displaying the chat SDK.',
          'Conversation management and workspace context: The inbox presents conversation previews and statuses alongside search, filters, and workspace selection. The application keeps the selected workspace, the requested conversation, and the visible state aligned as agents navigate.',
          'Broader operational workflows: The application includes email, comments, live visitors, post scheduling, settings, and statistics. Post scheduling combines draft and published states with calendar navigation.',
        ],
        screens: [
          {
            image: wisperbotAgentPostScheduling,
            caption: 'Figure 3: Post scheduling with search, status filters, list and calendar views',
          },
          {
            image: wisperbotAgentSettings,
            caption: 'Figure 4: Settings grouping account, appearance, and notification controls',
          },
        ],
        challenges: [
          {
            title: 'Workspace switching and state coordination',
            text: 'Coordinating workspace changes, notification navigation, API responses, and live updates. A response started in one context must never overwrite a newer context. By tokenizing active workspace requests and cancelling stale streams, state consistency is maintained.',
          },
        ],
      },
      {
        title: 'Releases, Outcomes and Lessons',
        paragraphs: [
          'I published the open source Flutter SDK on pub.dev and released the agent application on Google Play and the Apple App Store. These releases represent the delivery outcomes of the project.',
          'Build the case study around decisions another developer can reuse: a clear integration boundary, explicit ownership of live subscriptions, a consistent message reconciliation rule, and evidence for performance claims.',
        ],
        table: {
          headers: ['Deliverable', 'Outcome'],
          rows: [
            ['Flutter SDK', 'Published on pub.dev (wisperbot_chat) & open source'],
            ['Android application', 'Released on Google Play'],
            ['iOS application', 'Released on Apple App Store'],
          ],
        },
      },
    ],
  },
];
